<template>
  <div
    class="org-method-card"
    :class="{ 'org-method-card--expanded': expanded }"
    :data-test="dataTest || undefined"
  >
    <div class="org-method-card__header">
      <!-- Clickable body: navigate if pre-selected, else expand -->
      <div
        class="org-method-card__main"
        role="button"
        tabindex="0"
        @click="onMainClick"
        @keydown.enter.prevent="onMainClick"
      >
        <div class="org-method-card__icon">
          <i :class="['fa', icon]" aria-hidden="true"></i>
        </div>
        <div class="org-method-card__body">
          <span class="org-method-card__title">{{ displayTitle }}</span>
          <span class="org-method-card__subtitle">{{ displaySubtitle }}</span>
        </div>
      </div>

      <!-- Chevron: always toggles the list -->
      <button
        type="button"
        class="org-method-card__switcher"
        :aria-label="trans('auth.methodOrganisationSubtitle')"
        @click.stop="expanded = !expanded"
      >
        <i
          class="fa org-method-card__chevron"
          :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          aria-hidden="true"
        ></i>
      </button>
    </div>

    <Transition name="org-expand">
      <div v-if="expanded" class="org-method-card__list">
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="org-method-card__option"
          :class="{
            'org-method-card__option--selected': option.value === selected,
          }"
          :data-test="`login-method-${option.value}`"
          @click.stop="pick(option.value)"
        >
          <i
            class="fa org-method-card__option-icon"
            :class="
              option.value === selected ? 'fa-check-circle' : 'fa-circle-o'
            "
            aria-hidden="true"
          ></i>
          {{ option.title }}
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { lang } from "../../i18n";

const props = defineProps({
  options: { type: Array, required: true },
  lastUsed: { type: String, default: null },
  title: { type: String, default: null },
  subtitle: { type: String, default: null },
  icon: { type: String, default: "fa-building" },
  dataTest: { type: String, default: null },
});

const emit = defineEmits(["select"]);

const expanded = ref(false);
const selected = ref(null);

const selectedOption = computed(
  () => props.options.find((o) => o.value === selected.value) ?? null,
);

const displayTitle = computed(() =>
  selectedOption.value
    ? selectedOption.value.title
    : props.title ?? lang.get("auth.methodOrganisationTitle"),
);

const displaySubtitle = computed(() =>
  selectedOption.value
    ? lang.get("auth.lastUsed")
    : props.subtitle ?? lang.get("auth.methodOrganisationSubtitle"),
);

onMounted(() => {
  if (props.lastUsed && props.options.some((o) => o.value === props.lastUsed)) {
    selected.value = props.lastUsed;
  }
});

const onMainClick = () => {
  if (selectedOption.value) {
    emit("select", selected.value);
  } else {
    expanded.value = !expanded.value;
  }
};

const pick = (value) => {
  selected.value = value;
  expanded.value = false;
  emit("select", value);
};
</script>
